/**
 * Runtime helpers for media-element creation and CDN URL rewriting.
 *
 * Fabric represents videos via an `HTMLVideoElement` wrapped in a
 * `FabricImage` (no dedicated FabricVideo class). These helpers handle
 * the awkward bits: waiting for the element to actually load, swapping
 * S3 URLs to CDN ones, and disabling object caching so playback frames
 * don't get stuck on a stale snapshot.
 */
import { FabricImage } from 'fabric';
import { MediaElementReadyState } from '@/types/layers/media';
import type { StoredLayer } from '@/types/layers/layer';
import type { VideoLayerStyles } from '@/types/layers/layerStyles';
import type { ImageAspectType } from '@/types/layers/image';

// Cache-busted URLs, keyed by the original URL, so repeat loads of the
// same video (across multiple Ads) can reuse the browser's cache.
const videoCacheBusters = new Map<string, string>();

/**
 * Video creation
 */

/**
 * Create a hidden <video> element ready for FabricImage to render. The
 * returned element has its dimensions populated from the video metadata.
 */
export async function createVideoElementForCanvas(src: string) {
  const videoUrl = videoSourceUrl(src);
  const videoElement = document.createElement('video');
  videoElement.crossOrigin = 'anonymous';
  videoElement.src = videoUrl;
  await initializeMediaElement(videoElement);
  videoElement.width = videoElement.videoWidth;
  videoElement.height = videoElement.videoHeight;
  return videoElement;
}

/** The URL the editor loads a video from, so every element for it shares the browser cache. */
export function videoSourceUrl(src: string): string {
  return addCacheBuster(replaceWithCDN(src));
}

/**
 * Wrap a video (or placeholder canvas) element in a FabricImage with
 * object caching DISABLED. Caching would freeze the playback at the
 * first frame because Fabric re-uses the cached image instead of
 * reading the live element.
 */
export function createVideoFabricObject(
  element: HTMLVideoElement | HTMLCanvasElement,
  styles: Partial<VideoLayerStyles> = {}
) {
  const fabricObject = new FabricImage(element, {
    objectCaching: false,
    ...styles
  });
  return fabricObject;
}

/**
 * Image aspect
 */

/**
 * Determines the image aspect type (portrait, landscape, or square)
 * based on the provided dimensions string.
 *
 * The dimensions string is expected to contain width and height values
 * (e.g. "1920x1080"). If the dimensions are missing or invalid,
 * the function defaults to returning 'square'.
 */
export function getImageAspectType(dimensions?: string): ImageAspectType {
  const size = dimensions?.match(/\d+(\.\d+)?/g);
  return aspectTypeFromSize(Number(size?.[0]), Number(size?.[1]));
}

export function aspectTypeFromSize(
  width: number,
  height: number
): ImageAspectType {
  if (!width || !height) return 'square';

  const ratio = width / height;
  if (ratio < 0.85) return 'portrait';
  if (ratio > 1.15) return 'landscape';
  return 'square';
}

/**
 * Loads an image only to read its shape. Anything that fails or takes longer
 * than `timeoutMs` counts as square, so a slow image never holds up a layout.
 */
export function loadImageAspectType(
  src: string,
  timeoutMs: number
): Promise<ImageAspectType> {
  return new Promise(resolve => {
    const image = new Image();
    const timer = setTimeout(() => resolve('square'), timeoutMs);
    const finish = (type: ImageAspectType) => {
      clearTimeout(timer);
      resolve(type);
    };

    image.addEventListener(
      'load',
      () => finish(aspectTypeFromSize(image.naturalWidth, image.naturalHeight)),
      { once: true }
    );
    image.addEventListener('error', () => finish('square'), { once: true });
    image.src = src;
  });
}

/** An image's width divided by its height, or null when it cannot be loaded. */
export function loadImageRatio(src: string): Promise<number | null> {
  return new Promise(resolve => {
    const image = new Image();
    image.addEventListener(
      'load',
      () =>
        resolve(
          image.naturalHeight ? image.naturalWidth / image.naturalHeight : null
        ),
      { once: true }
    );
    image.addEventListener('error', () => resolve(null), { once: true });
    image.src = src;
  });
}

/* ── Internal ── */

/**
 * Cache busting
 */

/**
 * Append a cache-busting timestamp parameter to avoid net::ERR_CACHE_OPERATION_NOT_SUPPORTED
 * errors in Chrome when loading video files from S3/CDN with CORS anonymous mode.
 * Uses a stable cache buster per URL within the session so that subsequent loads of
 * the same video (across multiple Ads) can leverage the browser's cache.
 */
export function addCacheBuster(url: string): string {
  if (!url) return url;

  if (videoCacheBusters.has(url)) {
    return videoCacheBusters.get(url)!;
  }

  let cacheBustedUrl = url;
  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('cb', Date.now().toString());
    cacheBustedUrl = urlObj.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    cacheBustedUrl = `${url}${separator}cb=${Date.now()}`;
  }

  videoCacheBusters.set(url, cacheBustedUrl);
  return cacheBustedUrl;
}

/**
 * Helpers
 */

/**
 * Swap our S3 storage host for the CDN host when both are configured.
 * Cuts user-visible latency and avoids hitting the bucket directly.
 */
function replaceWithCDN(srcUrl: string) {
  const s3Url = `${import.meta.env.VITE_STORAGE_URL}/AllFiles/Uploads/`;
  const cdnUrl = import.meta.env.VITE_CB_GALLERY_URL;

  if (srcUrl && srcUrl.startsWith(s3Url) && cdnUrl) {
    const replacedUrl = srcUrl.replace(s3Url, cdnUrl);
    return replacedUrl;
  }

  return srcUrl;
}

/**
 * Resolve when the element has a drawable frame (or reject on error).
 * Already-loaded elements resolve immediately. Listeners are removed in
 * every branch so we don't leak handlers on retry.
 *
 * The bar is `loadeddata` (HAVE_CURRENT_DATA), not `canplaythrough`
 * (HAVE_ENOUGH_DATA): Fabric only needs one decoded frame to paint, while
 * `canplaythrough` waits for the browser to estimate the WHOLE clip can play
 * uninterrupted — effectively the full download. Browsers also throttle media
 * buffering in hidden tabs, so a background tab (cross-tab sync applying a
 * sibling's video add) can sit below HAVE_ENOUGH_DATA indefinitely and leave
 * the layer stuck on its "Loading video..." placeholder.
 *
 * `visibilitychange` covers the harder case: Chrome can park a hidden tab's
 * load before a single frame arrives, and it does not always resume on its own
 * — re-issuing `load()` once we're foreground kicks it back off.
 */
async function loadMediaElement(mediaElement: HTMLMediaElement): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (mediaElement.readyState >= MediaElementReadyState.HAVE_CURRENT_DATA) {
      resolve();
      return;
    }

    function onReady() {
      cleanup();
      resolve();
    }

    function onError() {
      cleanup();
      const error = mediaElement.error;
      reject(
        new Error(
          `Media load failed: ${error?.message || 'Unknown error'} (code: ${error?.code})`
        )
      );
    }

    function onVisibilityChange() {
      if (document.hidden) return;
      if (mediaElement.readyState >= MediaElementReadyState.HAVE_CURRENT_DATA) {
        onReady();
        return;
      }
      // Only restart a load that never got off the ground. One that already
      // has metadata is progressing — reloading it would throw that away.
      if (mediaElement.readyState === MediaElementReadyState.HAVE_NOTHING) {
        mediaElement.load();
      }
    }

    function cleanup() {
      mediaElement.removeEventListener('loadeddata', onReady);
      mediaElement.removeEventListener('canplaythrough', onReady);
      mediaElement.removeEventListener('error', onError);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    mediaElement.addEventListener('loadeddata', onReady, { once: true });
    mediaElement.addEventListener('canplaythrough', onReady, { once: true });
    mediaElement.addEventListener('error', onError, { once: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    mediaElement.load();
  });
}

// Set up a hidden, CORS-enabled media element and wait for it to load.
async function initializeMediaElement(mediaElement: HTMLMediaElement) {
  mediaElement.controls = false;
  mediaElement.style.display = 'none';
  mediaElement.crossOrigin = 'anonymous';
  mediaElement.preload = 'auto';
  await loadMediaElement(mediaElement);
}
